import { PlayerProperty, Save, Restore } from '../PlayerProperty';
import IPlayerData from '../../IPlayerData';
import {
    IPlayerState,
    IPlayerStateQuestData,
} from '../../../State/Player/IPlayerState';
import { restoreStringifiedArray } from '../../ParseHelper';
import { Quests as QuestTypeObject, QuestType } from '../../../State/Player/QuestType';
import { report } from '../../../report';
import QuestHelper from 'Utility/QuestHelper';

const questsKey = 'v1_51_0_quests' as const;
const stateKey = 'quests' as const;

type PlayerStateKey = typeof stateKey;
type PlayerDataKeys = typeof questsKey;

export default class Quests
    implements PlayerProperty<PlayerStateKey, PlayerDataKeys> {
    public isChanged(
        playerStateData: IPlayerState,
        playerEndpointData: IPlayerData
    ): boolean {
        return (
            this.save(playerStateData)[questsKey] !==
            playerEndpointData[questsKey]
        );
    }

    public restore(playerEndpointData: IPlayerData): Restore<PlayerStateKey> {
        const questDataValues = restoreStringifiedArray<number | string | null>(
            playerEndpointData[questsKey]
        );

        if (questDataValues) {
            const questData: IPlayerStateQuestData[] = [];
            for (let i = 0, n = questDataValues.length; i < n; i += 5) {
                try {
                    const type = QuestHelper.parseQuestType(questDataValues[i]);
                    let progress = QuestHelper.parseQuestProgress(questDataValues[i + 1]);
                    const cooldownStartedAt = QuestHelper.parseCooldownStartedAt(questDataValues[i + 2]);
                    const tier = QuestHelper.parseQuestTier(questDataValues[i + 3], type);
                    const id = QuestHelper.parseQuestId(questDataValues[i + 4]);

                    if (QuestHelper.isDeprecatedQuest(type)) {
                        progress = QuestHelper.getFinalQuestTarget(type, tier);
                    }

                    questData.push({
                        id,
                        progress,
                        tier,
                        type,
                        cooldownStartedAt,
                    });
                } catch (error) {
                    report('Failed to deserialize quests', {
                        error,
                        sampleRate: 0.1,
                        once: true,
                        tags: {
                            serialized: playerEndpointData[questsKey],
                        },
                        fingerprint: "restore:quests"
                    });
                }
            }
            return questData;
        }
        return [];
    }

    public save(playerStateData: IPlayerState): Save<PlayerDataKeys> {
        // Convert quest data array back to 'quests' array.
        const questDataValues: (number | QuestType | string)[] = [];
        for (const quest of playerStateData[stateKey]) {
            questDataValues.push(quest.type);
            questDataValues.push(quest.progress);
            questDataValues.push(quest.cooldownStartedAt?.getTime());
            questDataValues.push(quest.tier);
            questDataValues.push(quest.id);
        }

        return { [questsKey]: JSON.stringify(questDataValues) };
    }
}
